import { Reducer, useEffect, useReducer, useState } from "react"
import dayjs from "dayjs"

import {
  CheckoutAction,
  CheckoutActionType,
  CheckoutState,
  Discount,
  OtherOptionPayload,
  PaymentInitializerPayload,
} from "../types/checkout-state"
import { PaymentOption } from "../types/checkout"
import CheckoutServices from "@services/checkout.services"
import { useUserData } from "@lib/context/ComponentContext"

const initialState: CheckoutState = {
  //Initial state
  gross_price: 0,
  available_payment: {},

  // personal info
  fullname: "",
  user_phone_number: "",
  user_gender: 0,
  user_birthday: "",
  email: "",

  // Survey state
  info_from: [],
  other_option: "",
  is_other_option_selected: false,

  //error state
  error_discount: "",
  error_submitting: "",
  error_admin_fee: "",
  error_survey: "",
  error_personal_info: "",
  error_phone_number: "",

  //state for payment
  admin_fee: 0,
  selected_method: "",
  total_price: 0,

  //Promo
  promo_id: "",
  promo_code: "",
  discount_amount: 0,
  promo_name: "",
  applied_promo_code: "",
}

const reducer: Reducer<CheckoutState, CheckoutAction> = (state, action) => {
  const { type, payload } = action

  switch (type) {
    // initializer
    case CheckoutActionType.PaymentInitializer: {
      const initPayload = payload as PaymentInitializerPayload
      return {
        ...state,
        email: initPayload.email,
        gross_price: initPayload.price,
        total_price: initPayload.price + state.admin_fee,
        available_payment: initPayload.available_payment,
        fullname: initPayload?.fullname ?? "",
        user_phone_number: initPayload?.user_phone_number ?? "",
        user_gender: initPayload?.user_gender ?? 0,
        user_birthday: (initPayload?.user_birthday as string) ?? "",
      }
    }

    //  Payment Handler
    case CheckoutActionType.ChoosePayment: {
      const selectOption = payload as PaymentOption
      return {
        ...state,
        total_price: state.gross_price + selectOption.payment_fee - state.discount_amount,
        admin_fee: selectOption.payment_fee,
        selected_method: selectOption.payment_method,
      }
    }

    case CheckoutActionType.PersonalInfo: {
      const formPayload = payload as any
      return {
        ...state,
        [formPayload.key]: formPayload.value,
      }
    }

    case CheckoutActionType.InputSurvey: {
      const formPayload = payload as string

      let currentOptions = [...state.info_from]

      const findOption = currentOptions.find((c) => c === formPayload)

      if (findOption) {
        currentOptions = currentOptions.filter((c) => c !== findOption)
      } else {
        currentOptions = [...currentOptions, formPayload]
      }

      return {
        ...state,
        info_from: currentOptions,
      }
    }

    case CheckoutActionType.InputOtherOption: {
      const formPayload = payload as OtherOptionPayload
      return {
        ...state,
        ...formPayload,
      }
    }
    case CheckoutActionType.ErrorSurvey:
      return {
        ...state,
        error_survey: payload as string,
      }

    case CheckoutActionType.ErrorPhoneNumber:
      return {
        ...state,
        error_phone_number: payload as string,
      }

    case CheckoutActionType.InputPromoCode:
      return {
        ...state,
        promo_code: action.payload as string,
        applied_promo_code: action.payload as string,
      }

    case CheckoutActionType.ApplyPromo: {
      const promoPayload = payload as Discount

      return {
        ...state,
        modal_open: "closed",
        promo_code: "",
        error_discount: "",
        discount_amount: promoPayload.discount_deal,
        promo_id: promoPayload.id,
        promo_name: promoPayload.discount_name,
        total_price: state.gross_price + state.admin_fee - promoPayload.discount_deal,
      }
    }

    case CheckoutActionType.ErrorDiscount:
      return {
        ...state,
        error_discount: payload as string,
      }

    case CheckoutActionType.DeleteDiscount:
      return {
        ...state,
        discount_amount: 0,
        promo_name: "",
        total_price: state.gross_price + state.admin_fee,
      }

    // Payment
    case CheckoutActionType.ErrorSubmitting:
      return {
        ...state,
        error_submitting: payload as string,
      }

    // Payment
    case CheckoutActionType.ErrorPersonalInfo:
      return {
        ...state,
        error_personal_info: payload as string,
      }

    default:
      return state
  }
}

const useCheckout = (programId: string, price: number) => {
  const [checkoutState, checkoutDispatch] = useReducer(reducer, initialState)
  const [isLoading, setIsLoading] = useState(true)
  const { tokenData } = useUserData()
  dayjs().locale("id")

  const checkout = new CheckoutServices()

  function convertToDateOnly(isoString: string) {
    const date = new Date(isoString)

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0") // getMonth() returns 0-based month
    const day = String(date.getDate()).padStart(2, "0")

    return `${year}-${month}-${day}`
  }
  
  useEffect(() => {
    const getCheckoutData = async () => {
      try {
        const res = await checkout.getAvailablePayment(programId)
        if (res.isSuccess) {
          const data = res.getValue()
          checkoutDispatch({
            type: CheckoutActionType.PaymentInitializer,
            payload: {
              price,
              email: tokenData?.user_email as string,
              available_payment: data,
              fullname: tokenData?.fullname,
              user_phone_number: tokenData?.user_phone_number?.startsWith("+62")
                ? tokenData?.user_phone_number?.replace("+62", "")
                : tokenData?.user_phone_number,
              user_gender: tokenData?.user_gender,
              user_birthday: convertToDateOnly(tokenData?.user_birthday as string),
            },
          })
        }
        setIsLoading(false)
      } catch (e) {
        setIsLoading(false)
        if (process.env.NODE_ENV === "development") {
          console.log(e)
        }
      }
    }

    if (price) {
      getCheckoutData()
    }
  }, [price])

  return {
    checkoutState,
    checkoutDispatch,
    isLoading,
  }
}
export default useCheckout
